import date_utils from "./date_utils";
import { $, createSVG } from "./svg_utils";
import Bar from "./bar";
import Arrow from "./arrow";
import Popup from "./popup";

import "./gantt.css";

const VIEW_MODE = {
  DAY: "Day",
  MONTH: "Month",
};

const VIEW_MODE_PADDING = {
  DAY: ["0d", "1d"],
  MONTH: ["0m", "0m"],
};

const DEFAULT_OPTIONS = {
  header_height: 65,
  column_width: 30,
  view_modes: [...Object.values(VIEW_MODE)],
  bar_height: 8,
  bar_corner_radius: 0,
  arrow_curve: 5,
  padding: 2,
  view_mode: "Day",
  date_format: "YYYY-MM-DD",
  show_expected_progress: false,
  popup: false,
  language: "en",
  readonly: true,
  progress_readonly: false,
  dates_readonly: false,
  highlight_weekend: true,
  scroll_to: "start",
  lines: "horizontal",
  auto_move_label: true,
  today_button: false,
  view_mode_select: true,
  lineStart: null,
  lineEnd: null,
  lineCurrent: null,
  scrollTop: 0, //gantt的scrolltop
  table_row_height: 24.72,
};

function debounce(func, wait) {
  let timeout;

  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export default class Gantt {
  constructor(wrapper, tasks, options, onDateChange, onScrollY) {
    this.lineStart = options.lineStart?.format("YYYY-MM-DD");
    this.lineEnd = options.lineEnd?.format("YYYY-MM-DD");
    this.lineCurrent = options.lineCurrent?.format("YYYY-MM-DD");
    this.ganttDateStart = this.lineStart;
    this.ganttDateEnd = this.lineEnd;
    this.wrapper = wrapper;
    this.onDateChange = onDateChange;
    this.setup_wrapper(wrapper);
    this.setup_options(options);
    this.setup_tasks(tasks);
    this.onScrollY = onScrollY;
    this.handleScrollWithDebounce = debounce(this._handleScroll.bind(this), 120);
    // initialize with default view mode
    this.change_view_mode();
    this.bind_events();
  }

  createArrowMarker() {
    const marker = document.createElementNS("http://www.w3.org/2000/svg", "marker");
    marker.setAttribute("id", "LineArrow");
    marker.setAttribute("markerWidth", "6");
    marker.setAttribute("markerHeight", "6");
    marker.setAttribute("refX", "0");
    marker.setAttribute("refY", "3");
    marker.setAttribute("orient", "90deg");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M 0 0 L 6 3 L 0 6 z");
    path.setAttribute("fill", "red");

    marker.appendChild(path);
    document.querySelector(".gantt").appendChild(marker);
  }

  createHelperLine() {
    this.createArrowMarker();

    this.ganttChartContainer = document.querySelector(this.wrapper);
    this.helperLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
    this.helperLine.setAttribute("id", "helper-line");
    this.helperLine.setAttribute("class", "helper-line");
    this.helperLine.setAttribute("marker-start", "url(#LineArrow)");

    let defaultLineX = null;
    let startLinePx = null;
    let endLinePx = null;
    if (this.options.view_mode === "Day") {
      defaultLineX = parseFloat(document.querySelector(`.lower-text[id="${this.lineCurrent}"]`)?.style.left);
      startLinePx = document.querySelector(`.lower-text[id="${this.lineStart}"]`)?.style.left;
      endLinePx = document.querySelector(`.lower-text[id="${this.lineEnd}"]`)?.style.left;
    } else if (this.options.view_mode === "Month") {
      defaultLineX = parseFloat(document.querySelector(`.lower-text[id="${this.lineCurrent?.slice(0, 7) + "-01"}"]`)?.style.left);
      startLinePx = document.querySelector(`.lower-text[id="${this.lineStart?.slice(0, 7) + "-01"}"]`)?.style.left;
      endLinePx = document.querySelector(`.lower-text[id="${this.lineEnd?.slice(0, 7) + "-01"}"]`)?.style.left;
    }
    this.helperLine.setAttribute("x1", defaultLineX || 0);
    this.helperLine.setAttribute("y1", "65");
    this.helperLine.setAttribute("x2", defaultLineX || 0);
    this.helperLine.setAttribute("y2", "100%");
    document.querySelector(".gantt").appendChild(this.helperLine);

    let isDragging = false;

    this.helperLine.addEventListener("mousedown", (e) => {
      e.preventDefault();
      isDragging = true;
      document.body.style.cursor = "col-resize";
    });

    this.ganttChartContainer.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      this.svgRect = document.querySelector(".gantt").getBoundingClientRect();
      const clientWidth = this.ganttChartContainer.clientWidth;
      const currentScrollLeft = this.ganttChartContainer.scrollLeft;
      const newPosX = e.clientX - this.svgRect.left + currentScrollLeft;

      const minX = parseFloat(startLinePx);
      const maxX = parseFloat(endLinePx);
      if (newPosX >= minX && newPosX <= maxX) {
        const totalWidth = this.ganttChartContainer.scrollWidth - clientWidth;
        const scrollStep = 5;

        const relativePos = newPosX - currentScrollLeft;
        if (relativePos >= clientWidth - 50) {
          if (currentScrollLeft < totalWidth) {
            this.ganttChartContainer.scrollLeft += scrollStep;
          }
        } else if (relativePos <= 50) {
          if (currentScrollLeft > 0) {
            this.ganttChartContainer.scrollLeft -= scrollStep;
          }
        }
        const adjustedNewPosX = e.clientX - this.svgRect.left + this.ganttChartContainer.scrollLeft;
        this.helperLine.setAttribute("x1", `${adjustedNewPosX}px`);
        this.helperLine.setAttribute("x2", `${adjustedNewPosX}px`);
      }
    });

    this.helperLine.addEventListener("mouseup", () => {
      isDragging = false;
      document.body.style.cursor = "default";
      const currentX = parseFloat(this.helperLine.getAttribute("x1"));
      const minX = parseFloat(startLinePx);
      const maxX = parseFloat(endLinePx);

      const timeAxisElements = document.querySelectorAll(".lower-text");
      let closestElement = null;
      if (currentX > minX && currentX < maxX) {
        timeAxisElements.forEach((element) => {
          if (currentX > element.offsetLeft) {
            closestElement = element;
          }
        });
        if (closestElement) {
          const closestDateId = closestElement.id;
          if (this.onDateChange) {
            this.onDateChange(closestDateId);
          }
          const closestX = closestElement.offsetLeft;
          this.helperLine.setAttribute("x1", `${closestX}px`);
          this.helperLine.setAttribute("x2", `${closestX}px`);
        }
      }
    });

    this.helperLine.classList.add("helper-line");
    if (this.options.view_mode === "Day") {
      document.querySelector(`.lower-text[id="${this.lineCurrent}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    } else if (this.options.view_mode === "Month") {
      document
        .querySelector(`.lower-text[id="${this.lineCurrent?.slice(0, 7) + "-01"}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }

  setup_wrapper(element) {
    let svg_element, wrapper_element, container_element;

    // CSS Selector is passed
    if (typeof element === "string") {
      element = document.querySelector(element);
    }

    // get the SVGElement and container element
    if (element instanceof HTMLElement) {
      wrapper_element = element;
      svg_element = element.querySelector("svg");
      container_element = element.querySelector(".gantt-container");
    } else if (element instanceof SVGElement) {
      svg_element = element;
      container_element = element.closest(".gantt-container"); // Use closest to find the container
    } else {
      throw new TypeError(
        "Frappe Gantt only supports usage of a string CSS selector," + " HTML DOM element or SVG DOM element for the 'element' parameter"
      );
    }
    // svg element
    if (!svg_element) {
      // create it
      this.$svg = createSVG("svg", {
        append_to: wrapper_element,
        class: "gantt",
      });
    } else {
      this.$svg = svg_element;
      this.$svg.classList.add("gantt");
    }

    // Ensure the $container is created and appended to the parent element
    if (!container_element) {
      this.$container = document.createElement("div");
      this.$container.classList.add("gantt-container");

      const parent_element = this.$svg.parentElement;
      if (parent_element) {
        parent_element.appendChild(this.$container);
      }
    } else {
      this.$container = container_element;
    }

    // Ensure the $svg is appended to the $container
    if (this.$container && this.$svg.parentNode !== this.$container) {
      this.$container.appendChild(this.$svg);
    }

    // Ensure the $popup_wrapper is created and appended to the $container
    if (document.querySelector(".popup-wrapper")) {
      const popupWrapper = document.querySelector(".popup-wrapper");
      const ganttContainer = document.querySelector(".gantt-container");
      if (ganttContainer && ganttContainer.contains(popupWrapper)) {
        ganttContainer.removeChild(popupWrapper);
      }
    }
    if (!this.$popup_wrapper) {
      this.$popup_wrapper = document.createElement("div");
      this.$popup_wrapper.classList.add("popup-wrapper");
      this.$container.appendChild(this.$popup_wrapper);
      this.$popup_wrapper.style.opacity = 0;
    } else if (this.$popup_wrapper.parentNode !== this.$container) {
      this.$container.appendChild(this.$popup_wrapper);
    }

    this.setup_scroll_listener();
  }
  handleGanttScrollY(event) {
    const scrollTop = event.currentTarget.scrollTop;
    if (this.onScrollY) {
      this.onScrollY(scrollTop);
    }
  }

  _handleScroll(scrollTop) {
    if (this.onScrollY) {
      this.onScrollY(scrollTop);
    }
  }

  setup_scroll_listener() {
    if (this.$container) {
      this.$container.removeEventListener("scroll", this.handleGanttScrollY);
      this.$container.addEventListener("scroll", (e) => {
        this.handleScrollWithDebounce(e.currentTarget.scrollTop);
      });
    }
  }

  setup_options(options) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    if (!options.view_mode_padding) options.view_mode_padding = {};
    for (let [key, value] of Object.entries(options.view_mode_padding)) {
      if (typeof value === "string") {
        // Configure for single value given
        options.view_mode_padding[key] = [value, value];
      }
    }

    this.options.view_mode_padding = {
      ...VIEW_MODE_PADDING,
      ...options.view_mode_padding,
    };
  }

  setup_tasks(tasks) {
    // prepare tasks
    this.tasks = tasks.map((task, i) => {
      // convert to Date objects
      task._start = date_utils.parse(task.start);
      if (task.end === undefined && task.duration !== undefined) {
        task.end = task._start;
        let durations = task.duration.split(" ");

        durations.forEach((tmpDuration) => {
          let { duration, scale } = date_utils.parse_duration(tmpDuration);
          task.end = date_utils.add(task.end, duration, scale);
        });
      }
      task._end = date_utils.parse(task.end);
      let diff = date_utils.diff(task._end, task._start, "year");
      if (diff < 0) {
        alert("Start Date of task can't be after End Date of task.");
      }
      // make task invalid if duration too large
      if (date_utils.diff(task._end, task._start, "year") > 10) {
        task.end = null;
      }

      // cache index
      task._index = i;

      // invalid dates
      if (!task.start && !task.end) {
        const today = date_utils.today();
        task._start = today;
        task._end = date_utils.add(today, 2, "day");
      }

      if (!task.start && task.end) {
        task._start = date_utils.add(task._end, -2, "day");
      }

      if (task.start && !task.end) {
        task._end = date_utils.add(task._start, 2, "day");
      }

      // if hours is not set, assume the last day is full day
      // e.g: 2018-09-09 becomes 2018-09-09 23:59:59
      const task_end_values = date_utils.get_date_values(task._end);
      if (task_end_values.slice(3).every((d) => d === 0)) {
        task._end = date_utils.add(task._end, 24, "hour");
      }

      // invalid flag
      if (!task.start || !task.end) {
        task.invalid = true;
      }

      // dependencies
      if (typeof task.dependencies === "string" || !task.dependencies) {
        let deps = [];
        if (task.dependencies) {
          deps = task.dependencies
            .split(",")
            .map((d) => d.trim().replaceAll(" ", "_"))
            .filter((d) => d);
        }
        task.dependencies = deps;
      }

      // uids
      if (!task.id) {
        task.id = generate_id(task);
      } else if (typeof task.id === "string") {
        task.id = task.id.replaceAll(" ", "_");
      } else {
        task.id = `${task.id}`;
      }

      return task;
    });

    this.setup_dependencies();
  }

  setup_dependencies() {
    this.dependency_map = {};
    for (let t of this.tasks) {
      for (let d of t.dependencies) {
        this.dependency_map[d] = this.dependency_map[d] || [];
        this.dependency_map[d].push(t.id);
      }
    }
  }

  refresh(tasks) {
    this.setup_tasks(tasks);
    this.change_view_mode();
  }

  change_view_mode(mode = this.options.view_mode) {
    this.update_view_scale(mode);
    this.setup_dates();
    this.render();
    // fire viewmode_change event
    this.trigger_event("view_change", [mode]);
    this.createHelperLine();
  }

  update_view_scale(view_mode) {
    this.options.view_mode = view_mode;
    if (view_mode === VIEW_MODE.DAY) {
      this.options.step = 24;
      this.options.column_width = 30;
    } else if (view_mode === VIEW_MODE.MONTH) {
      this.options.step = 24 * 30;
      this.options.column_width = this.options.miniColumnWidth || 70;
    }
  }

  setup_dates() {
    this.setup_gantt_dates();
    this.setup_date_values();
  }

  setup_gantt_dates() {
    this.gantt_start = this.ganttDateStart ? date_utils.parse(this.ganttDateStart) : null;
    this.gantt_end = this.ganttDateEnd ? date_utils.parse(this.ganttDateEnd) : null;
    // 如果没有传入开始时间和结束时间，则根据任务的时间来确定
    if (!this.gantt_start || !this.gantt_end) {
      for (let task of this.tasks) {
        // set global start and end date
        if (!this.gantt_start || task._start < this.gantt_start) {
          this.gantt_start = task._start;
        }
        if (!this.gantt_end || task._end > this.gantt_end) {
          this.gantt_end = task._end;
        }
      }
    }

    // 确保 gantt_start 和 gantt_end 都是有效的 Date 对象
    if (!this.gantt_start) this.gantt_start = new Date();
    if (!this.gantt_end) this.gantt_end = new Date();

    // 将 gantt_start 和 gantt_end 调整到当天的开始和结束
    let gantt_start = date_utils.start_of(this.gantt_start, "day");
    let gantt_end = date_utils.start_of(this.gantt_end, "day");

    // 添加日期填充
    let viewKey;
    for (let [key, value] of Object.entries(VIEW_MODE)) {
      if (value === this.options.view_mode) {
        viewKey = key;
      }
    }
    const [padding_start, padding_end] = this.options.view_mode_padding[viewKey].map(date_utils.parse_duration);
    gantt_start = date_utils.add(gantt_start, -padding_start.duration, padding_start.scale);
    gantt_end = date_utils.add(gantt_end, padding_end.duration, padding_end.scale);

    // 格式化日期
    let format_string;
    if (this.view_is(VIEW_MODE.MONTH)) {
      format_string = "YYYY-MM";
    } else if (this.view_is(VIEW_MODE.DAY)) {
      format_string = "YYYY-MM-DD";
    } else {
      format_string = "YYYY-MM-DD HH";
    }
    this.gantt_start = date_utils.parse(date_utils.format(gantt_start, format_string));
    this.gantt_start.setHours(0, 0, 0, 0);
    this.gantt_end = gantt_end;
  }

  setup_date_values() {
    this.dates = [];
    let cur_date = null;

    while (cur_date === null || cur_date <= this.gantt_end) {
      if (!cur_date) {
        cur_date = date_utils.clone(this.gantt_start);
      } else {
        if (this.view_is(VIEW_MODE.MONTH)) {
          cur_date = date_utils.add(cur_date, 1, "month");
        } else {
          cur_date = date_utils.add(cur_date, this.options.step, "hour");
        }
      }
      this.dates.push(cur_date);
    }
  }

  bind_events() {
    if (this.options.readonly) return;
    this.bind_grid_click();
    this.bind_bar_events();
  }

  render() {
    this.clear();
    this.setup_layers();
    this.make_grid();
    this.make_dates();
    this.make_bars();
    this.make_grid_extras();
    this.make_arrows();
    this.map_arrows_on_bars();
    this.set_width();
    this.set_scroll_position(this.options.scroll_to);
  }

  setup_layers() {
    this.layers = {};
    const layers = ["grid", "arrow", "progress", "bar", "details"];
    // make group layers
    for (let layer of layers) {
      this.layers[layer] = createSVG("g", {
        class: layer,
        append_to: this.$svg,
      });
    }
  }

  make_grid() {
    this.make_grid_background();
    this.make_grid_rows();
    this.make_grid_header();
  }

  make_grid_extras() {
    // this.make_grid_highlights();
    this.make_grid_ticks();
  }

  make_grid_background() {
    // grid高度严格按table_row_height * tasks.length计算
    const grid_height = this.options.header_height + this.options.table_row_height * this.tasks.length / 2;
    createSVG("rect", {
      x: 0,
      y: 0,
      width: "100%",
      height: grid_height,
      class: "grid-background",
      append_to: this.$svg,
    });

    $.attr(this.$svg, {
      height: grid_height,
      width: "100%",
    });
  }

  make_grid_rows() {
    const rows_layer = createSVG("g", { append_to: this.layers.grid });
    const row_width = (this.dates.length + 1) * this.options.column_width;
    const grid_row_height = this.options.table_row_height / 2;
    let row_y = this.options.header_height;

    for (let i = 0; i < this.tasks.length; i++) {
      // 每行都是两个Bar
      const barsInRow = Array.isArray(this.tasks[i]) ? this.tasks[i] : [this.tasks[i]];
      for (let j = 0; j < barsInRow.length; j++) {
        createSVG("rect", {
          x: 0,
          y: row_y + j * grid_row_height,
          width: row_width,
          height: grid_row_height,
          class: "grid-row " + barsInRow[j].name,
          "data-id": barsInRow[j].name,
          append_to: rows_layer,
        });
      }
      row_y += this.options.table_row_height / 2;
    }
    const selectedRowId = this.options.selectedRowId;
    if (selectedRowId) {
      const selectedRows = document.querySelectorAll(`[data-id="${selectedRowId}"]`);
      selectedRows.forEach((row) => {
        row.style.fill = "#E1FEFF";
      });
    }
  }

  make_grid_header() {
    if (document.querySelector(".grid-header")) {
      const gridHeader = document.querySelector(".grid-header");
      const ganttContainer = document.querySelector(".gantt-container");
      if (ganttContainer && ganttContainer.contains(gridHeader)) {
        ganttContainer.removeChild(gridHeader);
      }
    }
    let $header = document.createElement("div");
    $header.style.height = this.options.header_height + "px";
    $header.style.width = (this.dates.length + 1) * this.options.column_width + "px";
    $header.classList.add("grid-header");
    this.$header = $header;
    this.$container.appendChild($header);

    let $upper_header = document.createElement("div");
    $upper_header.classList.add("upper-header");
    this.$upper_header = $upper_header;
    this.$header.appendChild($upper_header);

    let $lower_header = document.createElement("div");
    $lower_header.classList.add("lower-header");
    this.$lower_header = $lower_header;
    this.$header.appendChild($lower_header);

    // this.make_side_header();
  }

  make_side_header() {
    let $side_header = document.createElement("div");
    $side_header.classList.add("side-header");

    // Create view mode change radios
    if (this.options.view_mode_select) {
      const $modeRadios = document.createElement("div");
      $modeRadios.classList.add("mode-radios");

      const modes = ["Day", "Month"];
      modes.forEach((mode) => {
        const label = document.createElement("label");
        label.htmlFor = `mode-${mode.toLowerCase()}`;
        label.textContent = mode;

        const input = document.createElement("input");
        input.type = "radio";
        input.name = "view-mode";
        input.id = `mode-${mode.toLowerCase()}`;
        input.value = mode;
        input.checked = mode === this.options.view_mode;

        input.addEventListener("change", () => {
          this.change_view_mode(input.value);
        });

        label.appendChild(input);
        $modeRadios.appendChild(label);
      });

      $side_header.appendChild($modeRadios);
    }

    this.$header.appendChild($side_header);
    this.$side_header = $side_header;
  }

  make_grid_ticks() {
    if (!["both", "vertical", "horizontal"].includes(this.options.lines)) return;
    let tick_x = 0;
    let tick_y = this.options.header_height;
    let tick_height = this.options.table_row_height * this.tasks.length;

    let $lines_layer = createSVG("g", {
      class: "lines_layer",
      append_to: this.layers.grid,
    });

    let row_y = this.options.header_height;
    const row_width = (this.dates.length + 1) * this.options.column_width;

    if (this.options.lines !== "vertical") {
      for (let i = 0; i < this.tasks.length; i++) {
        // 横线严格按Table行高递增
        const line_y = row_y + this.options.table_row_height;
        createSVG("line", {
          x1: 0,
          y1: line_y,
          x2: row_width,
          y2: line_y,
          class: "row-line",
          append_to: $lines_layer,
        });
        row_y += this.options.table_row_height;
      }
    }
    if (this.options.lines === "horizontal") return;
    for (let date of this.dates) {
      let tick_class = "tick";
      // thick tick for monday
      if (this.view_is(VIEW_MODE.DAY) && date.getDate() === 1) {
        tick_class += " thick";
      }
      // thick tick for first week
      if (this.view_is(VIEW_MODE.WEEK) && date.getDate() >= 1 && date.getDate() < 8) {
        tick_class += " thick";
      }
      // thick ticks for quarters
      if (this.view_is(VIEW_MODE.MONTH) && date.getMonth() % 3 === 0) {
        tick_class += " thick";
      }

      createSVG("path", {
        d: `M ${tick_x} ${tick_y} v ${tick_height}`,
        class: tick_class,
        append_to: this.layers.grid,
      });

      if (this.view_is(VIEW_MODE.MONTH)) {
        tick_x += (date_utils.get_days_in_month(date) * this.options.column_width) / 30;
      } else {
        tick_x += this.options.column_width;
      }
    }
  }

  highlightWeekends() {
    if (!this.view_is("Day") && !this.view_is("Half Day")) return;
    for (let d = new Date(this.gantt_start); d <= this.gantt_end; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === 0 || d.getDay() === 6) {
        const x = (date_utils.diff(d, this.gantt_start, "hour") / this.options.step) * this.options.column_width;
        const height = (this.options.bar_height + this.options.padding) * this.tasks.length;
        createSVG("rect", {
          x,
          y: this.options.header_height + this.options.padding / 2,
          width: (this.view_is("Day") ? 1 : 2) * this.options.column_width,
          height,
          class: "holiday-highlight",
          append_to: this.layers.grid,
        });
      }
    }
  }

  //compute the horizontal x distance
  computeGridHighlightDimensions(view_mode) {
    let x = this.options.column_width / 2;

    if (this.view_is(VIEW_MODE.DAY)) {
      let today = date_utils.today();
      return {
        x: x + (date_utils.diff(today, this.gantt_start, "hour") / this.options.step) * this.options.column_width,
        date: today,
      };
    }

    for (let date of this.dates) {
      const todayDate = new Date();
      const startDate = new Date(date);
      const endDate = new Date(date);
      switch (view_mode) {
        case VIEW_MODE.WEEK:
          endDate.setDate(date.getDate() + 7);
          break;
        case VIEW_MODE.MONTH:
          endDate.setMonth(date.getMonth() + 1);
          break;
        case VIEW_MODE.YEAR:
          endDate.setFullYear(date.getFullYear() + 1);
          break;
      }
      if (todayDate >= startDate && todayDate <= endDate) {
        return { x, date: startDate };
      } else {
        x += this.options.column_width;
      }
    }

    return { x };
  }

  make_grid_highlights() {
    if (this.options.highlight_weekend) this.highlightWeekends();

    // highlight today's | week's | month's | year's
    if (this.view_is(VIEW_MODE.DAY) || this.view_is(VIEW_MODE.WEEK) || this.view_is(VIEW_MODE.MONTH) || this.view_is(VIEW_MODE.YEAR)) {
      // Used as we must find the _end_ of session if view is not Day
      const { x: left, date } = this.computeGridHighlightDimensions(this.options.view_mode);
      if (!date || !this.dates.find((d) => d.getTime() == date.getTime())) return;
      const top = this.options.header_height + this.options.padding / 2;
      const height = (this.options.bar_height + this.options.padding) * this.tasks.length;
      this.$current_highlight = this.create_el({
        top,
        left,
        height,
        classes: "current-highlight",
        append_to: this.$container,
      });
      let $today = document.getElementById(date_utils.format(date).replaceAll(" ", "_"));
      if ($today) {
        $today.classList.add("current-date-highlight");
        $today.style.top = +$today.style.top.slice(0, -2) - 4 + "px";
        $today.style.left = +$today.style.left.slice(0, -2) - 8 + "px";
      }
    }
  }

  create_el({ left, top, width, height, id, classes, append_to }) {
    let $el = document.createElement("div");
    $el.classList.add(classes);
    // $el.style.top = top + 'px';
    $el.style.left = left + "px";
    if (id) $el.id = id;
    if (width) $el.style.width = height + "px";
    if (height) $el.style.height = height + "px";
    append_to.appendChild($el);
    return $el;
  }

  make_dates() {
    this.upper_texts_x = {};
    this.get_dates_to_draw().forEach((date, i) => {
      let $lower_text = this.create_el({
        left: date.lower_x,
        top: date.lower_y,
        id: date.formatted_date,
        classes: "lower-text",
        append_to: this.$lower_header,
      });
      $lower_text.innerText = date.lower_text;
      $lower_text.style.left = +$lower_text.style.left.slice(0, -2) + "px";

      let $vertical_line = document.createElement("div");
      $vertical_line.classList.add("lower-vertical-line");
      $vertical_line.style.left = date.lower_x + this.options.column_width / 2 + "px";
      this.$lower_header.appendChild($vertical_line);

      if (date.upper_text) {
        this.upper_texts_x[date.upper_text] = date.upper_x;
        let $upper_text = document.createElement("div");
        $upper_text.classList.add("upper-text");
        $upper_text.style.left = date.upper_x + "px";
        $upper_text.innerText = date.upper_text;
        this.$upper_header.appendChild($upper_text);

        let $upper_vertical_line = document.createElement("div");
        $upper_vertical_line.classList.add("upper-vertical-line");
        if (this.options.view_mode === "Day") {
          $upper_vertical_line.style.left = date.upper_x - this.options.column_width / 2 + "px";
        } else {
          $upper_vertical_line.style.left = date.upper_x - 2 - this.options.column_width / 2 + "px";
        }
        this.$upper_header.appendChild($upper_vertical_line);

        // remove out-of-bound dates
        if (date.upper_x > this.layers.grid.getBBox().width) {
          $upper_text.remove();
          $upper_vertical_line.remove();
        }
      }
    });
  }

  get_dates_to_draw() {
    let last_date = null;
    const dates = this.dates.map((date, i) => {
      const d = this.get_date_info(date, last_date, i);
      last_date = d;
      return d;
    });
    return dates;
  }

  get_date_info(date, last_date_info) {
    let last_date = last_date_info ? last_date_info.date : date_utils.add(date, 1, "day");
    const date_text = {
      Day_lower: date.getDate() !== last_date.getDate() ? date_utils.format(date, "D", this.options.language) : "",
      Month_lower: date_utils.format(date, "MMM", this.options.language),
      Year_lower: date_utils.format(date, "YYYY", this.options.language),
      Day_upper: date.getMonth() !== last_date.getMonth() || !last_date_info ? date_utils.format(date, "MMM YYYY", this.options.language) : "",
      Month_upper: date.getFullYear() !== last_date.getFullYear() || !last_date_info ? date_utils.format(date, "YYYY", this.options.language) : "",
      Year_upper: date.getFullYear() !== last_date.getFullYear() ? date_utils.format(date, "YYYY", this.options.language) : "",
    };
    let column_width = this.view_is(VIEW_MODE.MONTH)
      ? (date_utils.get_days_in_month(date) * this.options.column_width) / 30
      : this.options.column_width;
    const base_pos = {
      x: last_date_info ? last_date_info.base_pos_x + last_date_info.column_width : 0,
      lower_y: this.options.header_height - 20,
      upper_y: this.options.header_height - 50,
    };
    const x_pos = {
      Day_lower: column_width / 2,
      Day_upper: column_width / 2,
      Month_lower: column_width / 2,
      Month_upper: column_width / 2,
      Year_lower: column_width / 2,
      Year_upper: (column_width * 30) / 2,
    };
    return {
      date,
      formatted_date: date_utils.format(date).split(" ")[0],
      column_width,
      base_pos_x: base_pos.x,
      upper_text: this.options.lower_text
        ? this.options.upper_text(date, this.options.view_mode, date_text[`${this.options.view_mode}_upper`])
        : date_text[`${this.options.view_mode}_upper`],
      lower_text: this.options.lower_text
        ? this.options.lower_text(date, this.options.view_mode, date_text[`${this.options.view_mode}_lower`])
        : date_text[`${this.options.view_mode}_lower`],
      upper_x: base_pos.x + x_pos[`${this.options.view_mode}_upper`],
      upper_y: base_pos.upper_y,
      lower_x: base_pos.x + x_pos[`${this.options.view_mode}_lower`],
      lower_y: base_pos.lower_y,
    };
  }

  make_bars() {
    this.bars = this.tasks.map((task) => {
      const bar = new Bar(this, task);
      this.layers.bar.appendChild(bar.group);
      return bar;
    });
  }

  make_arrows() {
    this.arrows = [];
    for (let task of this.tasks) {
      let arrows = [];
      arrows = task.dependencies
        .map((task_id) => {
          const dependency = this.get_task(task_id);
          if (!dependency) return;
          const arrow = new Arrow(
            this,
            this.bars[dependency._index], // from_task
            this.bars[task._index] // to_task
          );
          this.layers.arrow.appendChild(arrow.element);
          return arrow;
        })
        .filter(Boolean); // filter falsy values
      this.arrows = this.arrows.concat(arrows);
    }
  }

  map_arrows_on_bars() {
    for (let bar of this.bars) {
      bar.arrows = this.arrows.filter((arrow) => {
        return arrow.from_task.task.id === bar.task.id || arrow.to_task.task.id === bar.task.id;
      });
    }
  }

  set_width() {
    const cur_width = this.$svg.getBoundingClientRect().width;
    const actual_width = this.$svg.querySelector(".grid .grid-row") ? this.$svg.querySelector(".grid .grid-row").getAttribute("width") : 0;
    if (cur_width < actual_width) {
      this.$svg.setAttribute("width", actual_width);
    }
  }

  set_scroll_position(date) {
    if (!date || date === "start") {
      date = this.gantt_start;
    } else if (date === "today") {
      return this.scroll_today();
    } else if (typeof date === "string") {
      date = date_utils.parse(date);
    }

    const parent_element = this.$svg.parentElement;
    if (!parent_element) return;

    const hours_before_first_task = date_utils.diff(date, this.gantt_start, "hour") + 24;

    const scroll_pos = (hours_before_first_task / this.options.step) * this.options.column_width - this.options.column_width;
    parent_element.scrollTo({ left: scroll_pos, behavior: "smooth" });
  }

  scroll_today() {
    this.set_scroll_position(new Date());
  }

  bind_grid_click() {
    $.on(this.$svg, "click", ".grid-row, .grid-header", () => {
      this.unselect_all();
      this.hide_popup();
    });
  }

  bind_bar_events() {
    let is_dragging = false;
    let x_on_start = 0;
    let x_on_scroll_start = 0;
    let y_on_start = 0;
    let is_resizing_left = false;
    let is_resizing_right = false;
    let parent_bar_id = null;
    let bars = []; // instanceof Bar
    this.bar_being_dragged = null;

    function action_in_progress() {
      return is_dragging || is_resizing_left || is_resizing_right;
    }

    this.$svg.onclick = (e) => {
      if (e.target.classList.contains("grid-row")) this.unselect_all();
    };

    $.on(this.$svg, "mousedown", ".bar-wrapper, .handle", (e, element) => {
      const bar_wrapper = $.closest(".bar-wrapper", element);
      bars.forEach((bar) => bar.group.classList.remove("active"));

      if (element.classList.contains("left")) {
        is_resizing_left = true;
      } else if (element.classList.contains("right")) {
        is_resizing_right = true;
      } else if (element.classList.contains("bar-wrapper")) {
        is_dragging = true;
      }

      bar_wrapper.classList.add("active");
      if (this.popup) this.popup.parent.classList.add("hidden");

      if (this.popup) this.popup.parent.classList.add("hidden");

      x_on_start = e.offsetX || e.layerX;
      y_on_start = e.offsetY || e.layerY;

      parent_bar_id = bar_wrapper.getAttribute("data-id");
      const ids = [parent_bar_id, ...this.get_all_dependent_tasks(parent_bar_id)];
      bars = ids.map((id) => this.get_bar(id));

      this.bar_being_dragged = parent_bar_id;

      bars.forEach((bar) => {
        const $bar = bar.$bar;
        $bar.ox = $bar.getX();
        $bar.oy = $bar.getY();
        $bar.owidth = $bar.getWidth();
        $bar.finaldx = 0;
      });
    });
    $.on(this.$container, "scroll", (e) => {
      let elements = document.querySelectorAll(".bar-wrapper");
      let localBars = [];
      const ids = [];
      let dx;
      if (x_on_scroll_start) {
        dx = e.currentTarget.scrollLeft - x_on_scroll_start;
      }

      const daysSinceStart = ((e.currentTarget.scrollLeft / this.options.column_width) * this.options.step) / 24;
      let format_str = "D MMM";
      if (["Year", "Month"].includes(this.options.view_mode)) format_str = "YYYY";
      else if (["Day", "Week"].includes(this.options.view_mode)) format_str = "MMMM";
      else if (this.view_is("Half Day")) format_str = "D";
      else if (this.view_is("Hour")) format_str = "D MMMM";

      let currentUpper = date_utils.format(date_utils.add(this.gantt_start, daysSinceStart, "day"), format_str);
      const upperTexts = Array.from(document.querySelectorAll(".upper-text"));
      const $el = upperTexts.find((el) => el.textContent === currentUpper);
      if ($el && !$el.classList.contains("current-upper")) {
        const $current = document.querySelector(".current-upper");
        if ($current) {
          $current.classList.remove("current-upper");
          $current.style.left = this.upper_texts_x[$current.textContent] + "px";
          $current.style.top = this.options.header_height - 50 + "px";
        }

        $el.classList.add("current-upper");
        let dimensions = this.$svg.getBoundingClientRect();
        $el.style.left = dimensions.x + this.$container.scrollLeft + 10 + "px";
        $el.style.top = dimensions.y + this.options.header_height - 50 + "px";
      }

      Array.prototype.forEach.call(elements, function (el, i) {
        ids.push(el.getAttribute("data-id"));
      });

      if (dx) {
        localBars = ids.map((id) => this.get_bar(id));
        if (this.options.auto_move_label) {
          localBars.forEach((bar) => {
            bar.update_label_position_on_horizontal_scroll({
              x: dx,
              sx: e.currentTarget.scrollLeft,
            });
          });
        }
      }

      x_on_scroll_start = e.currentTarget.scrollLeft;
    });

    $.on(this.$svg, "mousemove", (e) => {
      if (!action_in_progress()) return;
      const dx = (e.offsetX || e.layerX) - x_on_start;

      bars.forEach((bar) => {
        const $bar = bar.$bar;
        $bar.finaldx = this.get_snap_position(dx);
        this.hide_popup();
        if (is_resizing_left) {
          if (parent_bar_id === bar.task.id) {
            bar.update_bar_position({
              x: $bar.ox + $bar.finaldx,
              width: $bar.owidth - $bar.finaldx,
            });
          } else {
            bar.update_bar_position({
              x: $bar.ox + $bar.finaldx,
            });
          }
        } else if (is_resizing_right) {
          if (parent_bar_id === bar.task.id) {
            bar.update_bar_position({
              width: $bar.owidth + $bar.finaldx,
            });
          }
        } else if (is_dragging && !this.options.readonly && !this.options.dates_readonly) {
          bar.update_bar_position({ x: $bar.ox + $bar.finaldx });
        }
      });
    });

    document.addEventListener("mouseup", (e) => {
      is_dragging = false;
      is_resizing_left = false;
      is_resizing_right = false;
    });

    $.on(this.$svg, "mouseup", (e) => {
      this.bar_being_dragged = null;
      bars.forEach((bar) => {
        const $bar = bar.$bar;
        if (!$bar.finaldx) return;
        bar.date_changed();
        bar.set_action_completed();
      });
    });

    this.bind_bar_progress();
  }

  bind_bar_progress() {
    let x_on_start = 0;
    let y_on_start = 0;
    let is_resizing = null;
    let bar = null;
    let $bar_progress = null;
    let $bar = null;

    $.on(this.$svg, "mousedown", ".handle.progress", (e, handle) => {
      is_resizing = true;
      x_on_start = e.offsetX || e.layerX;
      y_on_start = e.offsetY || e.layerY;

      const $bar_wrapper = $.closest(".bar-wrapper", handle);
      const id = $bar_wrapper.getAttribute("data-id");
      bar = this.get_bar(id);

      $bar_progress = bar.$bar_progress;
      $bar = bar.$bar;

      $bar_progress.finaldx = 0;
      $bar_progress.owidth = $bar_progress.getWidth();
      $bar_progress.min_dx = -$bar_progress.getWidth();
      $bar_progress.max_dx = $bar.getWidth() - $bar_progress.getWidth();
    });

    $.on(this.$svg, "mousemove", (e) => {
      if (!is_resizing) return;
      let dx = (e.offsetX || e.layerX) - x_on_start;

      if (dx > $bar_progress.max_dx) {
        dx = $bar_progress.max_dx;
      }
      if (dx < $bar_progress.min_dx) {
        dx = $bar_progress.min_dx;
      }

      const $handle = bar.$handle_progress;
      $.attr($bar_progress, "width", $bar_progress.owidth + dx);
      $.attr($handle, "cx", $bar_progress.getEndX());
      $bar_progress.finaldx = dx;
    });

    $.on(this.$svg, "mouseup", () => {
      is_resizing = false;
      if (!($bar_progress && $bar_progress.finaldx)) return;

      $bar_progress.finaldx = 0;
      bar.progress_changed();
      bar.set_action_completed();
      bar = null;
      $bar_progress = null;
      $bar = null;
    });
  }

  get_all_dependent_tasks(task_id) {
    let out = [];
    let to_process = [task_id];
    while (to_process.length) {
      const deps = to_process.reduce((acc, curr) => {
        acc = acc.concat(this.dependency_map[curr]);
        return acc;
      }, []);

      out = out.concat(deps);
      to_process = deps.filter((d) => !to_process.includes(d));
    }

    return out.filter(Boolean);
  }

  get_snap_position(dx) {
    let odx = dx,
      rem,
      position;

    if (this.view_is(VIEW_MODE.WEEK)) {
      rem = dx % (this.options.column_width / 7);
      position = odx - rem + (rem < this.options.column_width / 14 ? 0 : this.options.column_width / 7);
    } else if (this.view_is(VIEW_MODE.MONTH)) {
      rem = dx % (this.options.column_width / 30);
      position = odx - rem + (rem < this.options.column_width / 60 ? 0 : this.options.column_width / 30);
    } else {
      rem = dx % this.options.column_width;
      position = odx - rem + (rem < this.options.column_width / 2 ? 0 : this.options.column_width);
    }
    return position;
  }

  unselect_all() {
    [...this.$svg.querySelectorAll(".bar-wrapper")].forEach((el) => {
      el.classList.remove("active");
    });
    if (this.popup) this.popup.parent.classList.remove("hidden");
  }

  view_is(modes) {
    if (typeof modes === "string") {
      return this.options.view_mode === modes;
    }

    if (Array.isArray(modes)) {
      return modes.some((mode) => this.options.view_mode === mode);
    }

    return false;
  }

  get_task(id) {
    return this.tasks.find((task) => {
      return task.id === id;
    });
  }

  get_bar(id) {
    return this.bars.find((bar) => {
      return bar.task.id === id;
    });
  }

  show_popup(options) {
    if (this.options.popup === false) return;
    if (!this.popup) {
      this.popup = new Popup(this.$popup_wrapper, this.options.popup);
    }
    this.popup.show(options);
  }

  hide_popup() {
    this.popup && this.popup.hide();
  }

  trigger_event(event, args) {
    if (this.options["on_" + event]) {
      this.options["on_" + event].apply(this, args);
    }
  }

  /**
   * Gets the oldest starting date from the list of tasks
   *
   * @returns Date
   * @memberof Gantt
   */
  get_oldest_starting_date() {
    if (!this.tasks.length) return new Date();
    return this.tasks.map((task) => task._start).reduce((prev_date, cur_date) => (cur_date <= prev_date ? cur_date : prev_date));
  }

  /**
   * Clear all elements from the parent svg element
   *
   * @memberof Gantt
   */
  clear() {
    this.$svg.innerHTML = "";
    this.$header?.remove?.();
    this.$current_highlight?.remove?.();
    this.popup?.hide?.();
  }
}

Gantt.VIEW_MODE = VIEW_MODE;

function generate_id(task) {
  return task.name + "_" + Math.random().toString(36).slice(2, 12);
}
